// Enterprise chunked uploader client for /api/v1/uploads/*.
// Handles sign → chunked PUTs → complete with retries and resumability.
import { getDeviceId } from "./device";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V1 = `${BACKEND_URL}/api/v1`;

const uuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const dataUrlToBlob = (dataUrl) => {
  const [header, body] = dataUrl.split(",");
  const mime = /data:(.*?);base64/.exec(header)?.[1] || "image/jpeg";
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

/**
 * Upload a JPEG (provided as a data URL) via the v1 chunked-upload flow.
 *
 * @param {Object} params
 * @param {string} params.sessionId
 * @param {string} params.filename
 * @param {number} params.sequence
 * @param {string} params.tags
 * @param {string} params.dataUrl - "data:image/jpeg;base64,..."
 * @param {Object} [params.metadata]
 * @param {string} [params.idempotencyKey] - reuse to dedup retries
 * @param {(progress: {sent: number, total: number}) => void} [params.onProgress]
 * @returns {Promise<{photo_id: string, upload_id: string, size_bytes: number}>}
 */
export const uploadChunked = async ({
  sessionId,
  filename,
  sequence,
  tags,
  dataUrl,
  metadata = {},
  idempotencyKey,
  onProgress,
}) => {
  const blob = dataUrlToBlob(dataUrl);
  const total = blob.size;
  const idemSign = idempotencyKey || uuid();
  const idemComplete = uuid();

  // 1) Sign
  const signResp = await fetch(`${API_V1}/uploads/sign`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", "Idempotency-Key": idemSign, "X-Device-Id": getDeviceId() },
    body: JSON.stringify({
      session_id: sessionId,
      filename,
      sequence,
      tags,
      total_bytes: total,
      content_type: blob.type || "image/jpeg",
      chunk_bytes: 900 * 1024, // most captures fit one round trip (stays under 1 MB proxy body limits)
      metadata,
    }),
  });
  if (!signResp.ok) throw new Error(`sign failed: ${signResp.status}`);
  const sign = await signResp.json();
  const { upload_id, token, chunk_bytes } = sign;
  // Build from REACT_APP_BACKEND_URL: server-returned absolute URLs use the
  // internal Host/scheme behind the ingress (http://…cluster…) → Mixed Content.
  const chunk_url_template = `${API_V1}/uploads/${upload_id}/chunk?offset={offset}`;
  const complete_url = `${API_V1}/uploads/${upload_id}/complete`;

  // 2) PUT chunks
  let offset = 0;
  while (offset < total) {
    const end = Math.min(offset + chunk_bytes, total);
    const slice = blob.slice(offset, end);
    const url = chunk_url_template.replace("{offset}", String(offset));
    let attempt = 0;
    while (true) {
      try {
        const r = await fetch(url, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}` },
          body: slice,
        });
        if (!r.ok) throw new Error(`chunk ${offset} failed: ${r.status}`);
        break;
      } catch (e) {
        attempt += 1;
        if (attempt >= 3) throw e;
        await new Promise((res) => setTimeout(res, 500 * attempt));
      }
    }
    offset = end;
    onProgress?.({ sent: offset, total });
  }

  // 3) Complete
  const completeResp = await fetch(complete_url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Idempotency-Key": idemComplete,
    },
  });
  if (!completeResp.ok) throw new Error(`complete failed: ${completeResp.status}`);
  return await completeResp.json();
};

/** Resume a previously-started upload by fetching its state. */
export const getUploadState = async (uploadId) => {
  const r = await fetch(`${API_V1}/uploads/${uploadId}`, { credentials: "include", headers: { "X-Device-Id": getDeviceId() } });
  if (!r.ok) throw new Error(`state failed: ${r.status}`);
  return await r.json();
};
